import React, { useState, useEffect } from 'react';
import { 
    Card, 
    Button, 
    Table, 
    Modal, 
    Form, 
    Input, 
    Select, 
    Switch, 
    message, 
    Space, 
    Tag, 
    Popconfirm,
    Alert,
    Divider,
    Row,
    Col,
    Typography,
    Tabs,
    Badge,
    Tooltip,
    Drawer
} from 'antd';
import { 
    PlusOutlined, 
    EditOutlined, 
    DeleteOutlined, 
    SendOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    SettingOutlined,
    EyeOutlined,
    CopyOutlined,
    MailOutlined,
    BarChartOutlined
} from '@ant-design/icons';
import api from '../../../services/api';
import { getStatusColor } from '../../../utils/ui';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;

function EmailTemplates() {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [form] = Form.useForm();
    const [categories, setCategories] = useState({});
    const [triggerEvents, setTriggerEvents] = useState({});
    const [templateVariables, setTemplateVariables] = useState({});
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [testModalVisible, setTestModalVisible] = useState(false);
    const [stats, setStats] = useState({});
    const [activeTab, setActiveTab] = useState('list');

    useEffect(() => {
        loadTemplates();
        loadCategories();
        loadStats();
    }, []);

    const loadTemplates = async () => {
        setLoading(true);
        try {
            const response = await api.get('/admin/email-templates');
            setTemplates(response.data.data || []);
            setCategories(response.data.categories || {});
            setTriggerEvents(response.data.trigger_events || {});
        } catch (error) {
            message.error('Failed to load email templates');
        } finally {
            setLoading(false);
        }
    };

    const loadCategories = async () => {
        try {
            const response = await api.get('/admin/email-templates/variables');
            setTemplateVariables(response.data.data || {});
        } catch (error) {
            console.error('Failed to load template variables:', error);
        }
    };

    const loadStats = async () => {
        try {
            const response = await api.get('/admin/email-templates/stats');
            setStats(response.data.data || {});
        } catch (error) {
            console.error('Failed to load stats:', error);
        }
    };

    const openModal = (template = null) => {
        setEditingTemplate(template);
        if (template) {
            form.setFieldsValue({
                ...template,
                variables: template.variables || [],
            });
        } else {
            form.resetFields();
            form.setFieldsValue({
                is_active: true,
                is_default: false,
                sort_order: 0,
            });
        }
        setModalVisible(true);
    };

    const handleSubmit = async (values) => {
        try {
            setLoading(true);
            
            if (editingTemplate) {
                await api.put(`/admin/email-templates/${editingTemplate.id}`, values);
                message.success('Email template updated successfully');
            } else {
                await api.post('/admin/email-templates', values);
                message.success('Email template created successfully');
            }
            
            setModalVisible(false);
            form.resetFields();
            loadTemplates();
            loadStats();
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to save email template');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await api.delete(`/admin/email-templates/${id}`);
            message.success('Email template deleted successfully');
            loadTemplates();
            loadStats();
        } catch (error) {
            message.error('Failed to delete email template');
        }
    };

    const handleDuplicate = async (template) => {
        try {
            await api.post(`/admin/email-templates/${template.id}/duplicate`);
            message.success('Email template duplicated successfully');
            loadTemplates();
        } catch (error) {
            message.error('Failed to duplicate email template');
        }
    };

    const handlePreview = async (template) => {
        try {
            const response = await api.post(`/admin/email-templates/${template.id}/preview`, {
                sample_data: getSampleData(template.category)
            });
            setPreviewData(response.data.data);
            setPreviewVisible(true);
        } catch (error) {
            message.error('Failed to preview template');
        }
    };

    const handleTest = async (template) => {
        setEditingTemplate(template);
        setTestModalVisible(true);
    };

    const sendTestEmail = async (values) => {
        try {
            await api.post(`/admin/email-templates/${editingTemplate.id}/test`, {
                test_email: values.test_email,
                sample_data: getSampleData(editingTemplate.category)
            });
            message.success('Test email sent successfully');
            setTestModalVisible(false);
        } catch (error) {
            message.error('Failed to send test email');
        }
    };

    const getSampleData = (category) => {
        const sampleData = {
            user: {
                user_name: 'John Doe',
                user_email: 'john@example.com',
                app_name: 'Spoon & Spice',
                app_url: 'https://spoonandspice.com'
            },
            vendor: {
                vendor_name: 'Delicious Catering',
                vendor_email: 'vendor@example.com',
                app_name: 'Spoon & Spice',
                dashboard_url: 'https://spoonandspice.com/vendor/dashboard'
            },
            booking: {
                customer_name: 'Jane Smith',
                booking_reference: 'BK-2024-001',
                event_date: '2024-12-25',
                vendor_name: 'Delicious Catering',
                total_amount: '15000'
            },
            admin: {
                name: 'John Doe',
                email: 'john@example.com',
                subject: 'General Inquiry',
                message: 'This is a sample message',
                app_name: 'Spoon & Spice'
            }
        };
        return sampleData[category] || {};
    };

    const getCategoryColor = (category) => {
        const colors = {
            user: 'blue',
            vendor: 'green',
            admin: 'red',
            system: 'orange',
            marketing: 'purple',
            booking: 'cyan',
            notification: 'magenta'
        };
        return colors[category] || 'default';
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <div>
                    <Text strong>{text}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        {record.description}
                    </Text>
                </div>
            ),
        },
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
            render: (category) => (
                <Tag color={getCategoryColor(category)}>
                    {categories[category] || category}
                </Tag>
            ),
        },
        {
            title: 'Trigger Event',
            dataIndex: 'trigger_event',
            key: 'trigger_event',
            render: (event) => event ? (
                <Tag>{triggerEvents[event] || event}</Tag>
            ) : '-',
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => (
                <Space>
                    <Tag color={getStatusColor(record.is_active ? 'success' : 'cancelled')}>
                        {record.is_active ? 'Active' : 'Inactive'}
                    </Tag>
                    {record.is_default && (
                        <Tag color="blue">Default</Tag>
                    )}
                </Space>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Tooltip title="Preview">
                        <Button 
                            icon={<EyeOutlined />} 
                            size="small" 
                            onClick={() => handlePreview(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Test Email">
                        <Button 
                            icon={<MailOutlined />} 
                            size="small" 
                            onClick={() => handleTest(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Edit">
                        <Button 
                            icon={<EditOutlined />} 
                            size="small" 
                            onClick={() => openModal(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Duplicate">
                        <Button 
                            icon={<CopyOutlined />} 
                            size="small" 
                            onClick={() => handleDuplicate(record)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Are you sure you want to delete this template?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Tooltip title="Delete">
                            <Button 
                                icon={<DeleteOutlined />} 
                                size="small" 
                                danger
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div style={{ marginBottom: 24 }}>
                <Title level={2}>Email Templates</Title>
                <Text type="secondary">
                    Manage email templates for all system operations and communications
                </Text>
            </div>

            <Tabs activeKey={activeTab} onChange={setActiveTab}>
                <TabPane tab="Templates" key="list">
                    <Card>
                        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                            <Space>
                                <Button 
                                    type="primary" 
                                    icon={<PlusOutlined />} 
                                    onClick={() => openModal()}
                                >
                                    Create Template
                                </Button>
                                <Button 
                                    icon={<BarChartOutlined />} 
                                    onClick={loadStats}
                                >
                                    Refresh Stats
                                </Button>
                            </Space>
                        </div>

                        <Table
                            columns={columns}
                            dataSource={templates}
                            rowKey="id"
                            loading={loading}
                            pagination={{
                                pageSize: 10,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total, range) => 
                                    `${range[0]}-${range[1]} of ${total} templates`,
                            }}
                        />
                    </Card>
                </TabPane>

                <TabPane tab="Statistics" key="stats">
                    <Row gutter={16}>
                        <Col xs={24} sm={12} lg={6}>
                            <Card>
                                <div style={{ textAlign: 'center' }}>
                                    <Title level={3} style={{ color: '#1890ff' }}>
                                        {stats.total_templates || 0}
                                    </Title>
                                    <Text type="secondary">Total Templates</Text>
                                </div>
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <Card>
                                <div style={{ textAlign: 'center' }}>
                                    <Title level={3} style={{ color: '#52c41a' }}>
                                        {stats.active_templates || 0}
                                    </Title>
                                    <Text type="secondary">Active Templates</Text>
                                </div>
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <Card>
                                <div style={{ textAlign: 'center' }}>
                                    <Title level={3} style={{ color: '#faad14' }}>
                                        {Object.keys(categories).length}
                                    </Title>
                                    <Text type="secondary">Categories</Text>
                                </div>
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <Card>
                                <div style={{ textAlign: 'center' }}>
                                    <Title level={3} style={{ color: '#f5222d' }}>
                                        {Object.keys(triggerEvents).length}
                                    </Title>
                                    <Text type="secondary">Trigger Events</Text>
                                </div>
                            </Card>
                        </Col>
                    </Row>

                    {stats.templates_by_category && (
                        <Card title="Templates by Category" style={{ marginTop: 16 }}>
                            <Row gutter={16}>
                                {Object.entries(stats.templates_by_category).map(([category, count]) => (
                                    <Col xs={12} sm={8} md={6} key={category}>
                                        <div style={{ textAlign: 'center', padding: 16 }}>
                                            <Badge 
                                                count={count} 
                                                style={{ backgroundColor: '#1890ff' }}
                                            />
                                            <br />
                                            <Text strong>{categories[category] || category}</Text>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </Card>
                    )}
                </TabPane>
            </Tabs>

            {/* Create/Edit Modal */}
            <Modal
                title={editingTemplate ? 'Edit Email Template' : 'Create Email Template'}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={800}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="name"
                                label="Template Name"
                                rules={[{ required: true, message: 'Please enter template name' }]}
                            >
                                <Input placeholder="e.g., Welcome Email" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="category"
                                label="Category"
                                rules={[{ required: true, message: 'Please select category' }]}
                            >
                                <Select placeholder="Select category">
                                    {Object.entries(categories).map(([key, value]) => (
                                        <Option key={key} value={key}>{value}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="subject"
                        label="Email Subject"
                        rules={[{ required: true, message: 'Please enter email subject' }]}
                    >
                        <Input placeholder="e.g., Welcome to {{app_name}}!" />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Description"
                    >
                        <TextArea rows={2} placeholder="Brief description of this template" />
                    </Form.Item>

                    <Form.Item
                        name="html_content"
                        label="HTML Content"
                        rules={[{ required: true, message: 'Please enter HTML content' }]}
                    >
                        <TextArea 
                            rows={10} 
                            placeholder="Enter HTML content with variables like {{user_name}}"
                        />
                    </Form.Item>

                    <Form.Item
                        name="text_content"
                        label="Plain Text Content"
                    >
                        <TextArea 
                            rows={6} 
                            placeholder="Enter plain text version (optional)"
                        />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item
                                name="trigger_event"
                                label="Trigger Event"
                            >
                                <Select placeholder="Select trigger event" allowClear>
                                    {Object.entries(triggerEvents).map(([key, value]) => (
                                        <Option key={key} value={key}>{value}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="sort_order"
                                label="Sort Order"
                            >
                                <Input type="number" placeholder="0" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="is_active"
                                label="Active"
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={loading}>
                                {editingTemplate ? 'Update' : 'Create'} Template
                            </Button>
                            <Button onClick={() => setModalVisible(false)}>
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Preview Drawer */}
            <Drawer
                title="Template Preview"
                placement="right"
                width={600}
                open={previewVisible}
                onClose={() => setPreviewVisible(false)}
            >
                {previewData && (
                    <div>
                        <Title level={4}>Subject: {previewData.subject}</Title>
                        <Divider />
                        <div 
                            dangerouslySetInnerHTML={{ __html: previewData.html_content }}
                            style={{ 
                                border: '1px solid #d9d9d9', 
                                padding: 16, 
                                borderRadius: 6,
                                backgroundColor: '#fafafa'
                            }}
                        />
                    </div>
                )}
            </Drawer>

            {/* Test Email Modal */}
            <Modal
                title="Send Test Email"
                open={testModalVisible}
                onCancel={() => setTestModalVisible(false)}
                footer={null}
            >
                <Form
                    layout="vertical"
                    onFinish={sendTestEmail}
                >
                    <Form.Item
                        name="test_email"
                        label="Test Email Address"
                        rules={[
                            { required: true, message: 'Please enter test email' },
                            { type: 'email', message: 'Please enter valid email' }
                        ]}
                    >
                        <Input placeholder="test@example.com" />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" icon={<SendOutlined />}>
                                Send Test Email
                            </Button>
                            <Button onClick={() => setTestModalVisible(false)}>
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}

export default EmailTemplates;
